import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

const PRIMARY_COLOR = "#ebcab3"; // background
const ACCENT_COLOR = "#3d0707"; // brand accent

function Portal() {
  const [id_number, setIdNumber] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    if (!id_number || !password) {
      setError("Both fields are required");
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch("https://portal.lsb.go.ke/api/token/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_number, password }),
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || "Invalid ID number or password");
        return;
      }

      localStorage.setItem("accessToken", data.access);
      localStorage.setItem("refreshToken", data.refresh);
      localStorage.setItem("account_type", data.account_type);
      localStorage.setItem("userDetails", JSON.stringify({ idNumber: id_number }));

      if (!data.password) {
        navigate("/dashboard");
      } else if (data.profile) {
        // Double check steps even if profile is true
        try {
          const stepsResponse = await fetch("https://portal.lsb.go.ke/account/steps/", {
            method: "GET",
            headers: {
              "Authorization": `Bearer ${data.access}`,
              "Content-Type": "application/json",
            },
          });

          if (stepsResponse.ok) {
            const stepsData = await stepsResponse.json();
            const allStepsCompleted = Object.values(stepsData).every(step => step === true);
            if (allStepsCompleted) {
              navigate("/finalDashL");
            } else {
              navigate("/maindashboard");
            }
          } else {
            navigate("/finalDashL"); // Fallback if steps check fails but profile is true
          }
        } catch (err) {
          console.error("Error checking steps:", err);
          navigate("/finalDashL"); // Fallback
        }
      } else {
        // If profile is false, redirect to step 6 (index 5)
        navigate("/maindashboard", { state: { initialStep: 5 } });
      }
    } catch (err) {
      setError("Login failed: Invalid ID number or password");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="flex items-center justify-center py-16"
      style={{
        background: `linear-gradient(35deg, ${PRIMARY_COLOR} 20%, #fff 80%)`,
      }}
    >
      <div
        className="mx-auto w-full max-w-md shadow-2xl"
        style={{
          background: "#fff",
          borderRadius: "16px",
          boxShadow: `0 8px 36px rgba(61,7,7,0.13), 0 1.5px 6px #ebcab3`,
          border: `3px solid ${ACCENT_COLOR}`,
        }}
      >
        <h3 className="text-center font-bold pt-8 pb-1 uppercase">
          Portal
        </h3>

        <div className="w-full flex justify-center pb-2">
          <span style={{ width: 80, borderBottom: `3px solid ${PRIMARY_COLOR}`, display: "block", borderRadius: 4 }}></span>
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-5 px-8 pb-8 pt-3"
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-center mb-3"
            >
              <span
                style={{
                  color: ACCENT_COLOR,
                  background: "#ebcab345",
                  padding: "10px 12px",
                  borderRadius: 8,
                  fontWeight: 700,
                  fontSize: ".98em",
                  display: "inline-block",
                  border: `1px solid ${ACCENT_COLOR}`,
                  letterSpacing: ".4px",
                }}
              >
                {error}
              </span>
            </motion.div>
          )}

          {/* ID Number */}
          <div>
            <label htmlFor="id_number" className="block text-sm font-semibold mb-1" style={{ color: ACCENT_COLOR }}>
              ID Number
            </label>
            <div className="relative">
              <input
                type="number"
                name="id_number"
                id="id_number"
                autoComplete="username"
                value={id_number}
                onChange={(e) => setIdNumber(e.target.value)}
                className="w-full rounded-lg border shadow-sm py-3 pl-4 pr-11 text-sm transition focus:outline-none"
                style={{
                  border: `1.5px solid ${PRIMARY_COLOR}`,
                  background: "#fff8f3",
                  color: ACCENT_COLOR,
                }}
                placeholder="Enter your ID Number"
                required
              />
              <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#b27849] text-lg">
                <i className="fa-regular fa-id-badge"></i>
              </span>
            </div>
          </div>

          {/* Password */}
          <div>
            <label htmlFor="password" className="block text-sm font-semibold mb-1" style={{ color: ACCENT_COLOR }}>
              Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                name="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border shadow-sm py-3 pl-4 pr-11 text-sm transition focus:outline-none"
                style={{
                  border: `1.5px solid ${PRIMARY_COLOR}`,
                  background: "#fff8f3",
                  color: ACCENT_COLOR,
                  letterSpacing: ".06em",
                }}
                placeholder="Enter your password"
                required
              />
              <button
                type="button"
                aria-label={showPassword ? "Hide password" : "Show password"}
                onClick={togglePasswordVisibility}
                className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#b27849] text-lg focus:outline-none"
                tabIndex={-1}
                style={{ background: "transparent" }}
              >
                {showPassword ? (
                  <i className="fa-regular fa-eye" />
                ) : (
                  <i className="fa-regular fa-eye-slash" />
                )}
              </button>
            </div>
          </div>

          {/* Submit Button */}
          <div className="pt-3">
            <button
              type="submit"
              className="w-full rounded font-bold py-3 px-7 uppercase tracking-wide text-lg transition"
              style={{
                background: ACCENT_COLOR,
                color: PRIMARY_COLOR,
                boxShadow: "0 3px 18px #3d07071c",
                letterSpacing: ".08em",
                border: `2.5px solid ${PRIMARY_COLOR}`,
              }}
              disabled={isLoading}
            >
              {isLoading ? (
                <span
                  className="inline-block align-middle"
                  style={{
                    width: 24,
                    height: 24,
                    border: `3px solid ${PRIMARY_COLOR}`,
                    borderTop: `3px solid ${ACCENT_COLOR}`,
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                  }}
                />
              ) : (
                "Sign In"
              )}
            </button>
          </div>

          {/* reset password */}
          <div className="flex justify-center items-center pt-2 pb-1">
            <span>Forgot password? <a href="/resetmail">Click here to reset</a></span>
          </div>
        </form>
      </div>
      <style>{`
        @media (max-width: 600px) {
          .max-w-md { max-width: 97vw; }
          .px-8 { padding-left: 1.25rem; padding-right: 1.25rem;}
        }
        @keyframes spin {
          from { transform: rotate(0deg);}
          to { transform: rotate(360deg);}
        }
      `}</style>
    </div>
  );
}

export default Portal;